import { useCallback, useContext, useMemo } from 'react';

import { SlabContext } from '../context/SlabContext';
import { NewQuotePolicy, QuotePolicy } from '../types/api/QuotePolicy/QuotePolicy';
import { NewQuoteStatus, QuoteStatus } from '../types/api/QuoteStatus/QuoteStatus';
import type { LookupInputOption } from '../types/inputOption';
import { List } from '../types/list';
import { lookups } from '../utils/domainHelpers';
import { hasValue } from '../utils/equality';
import { getTenantRequiresApprovals } from '../utils/tenantConfig';

type UseQuoteStatusOptionsProps = {
  quoteStatuses: List<QuoteStatus> | undefined;
  quotePolicies: List<QuotePolicy> | undefined;
};

type UseQuoteStatusOptionsReturn = {
  // Returns a list of options for the quote status dropdown.
  // The options are filtered based on the current quote status.
  // For a new quote, provide the current quote status as undefined.
  getStatusOptions: (currentQuoteStatus?: QuoteStatus) => LookupInputOption[];
};

// This hook returns options for the quote status dropdown.
export const useQuoteStatusOptions = ({
  quoteStatuses = new List<QuoteStatus>(NewQuoteStatus, { items: [], count: 0 }),
  quotePolicies = new List<QuotePolicy>(NewQuotePolicy, { items: [], count: 0 }),
}: UseQuoteStatusOptionsProps): UseQuoteStatusOptionsReturn => {
  const ctx = useContext(SlabContext);
  const tenantRequiresApprovals = useMemo(
    () => getTenantRequiresApprovals({ tenant: ctx?.userInfo?.tenant, quotePolicies }),
    [ctx?.userInfo?.tenant, quotePolicies],
  );

  const getStatusOptions = useCallback(
    (currentQuoteStatus?: QuoteStatus) => {
      let statuses = quoteStatuses.items;
      if (tenantRequiresApprovals) {
        if (hasValue(currentQuoteStatus)) {
          statuses = statuses?.filter((status) => currentQuoteStatus.canTransitionTo(status));
        } else {
          statuses = statuses?.filter((status) => status.isDraft());
        }
      }
      return lookups({
        items: statuses,
        label: (status) => status.name,
        count: statuses.length,
      });
    },
    [quoteStatuses, tenantRequiresApprovals],
  );

  return {
    getStatusOptions,
  };
};
